import { api, noteRequestId } from './client';
import { RyptError, newRequestId, type CallCtx } from './errors';
import { AUDIT_DEFAULT_LIMIT } from './audit-cursor';
import type {
  Action,
  ApiToken,
  ApiTokenRestrictions,
  AuditEvent,
  Billing,
  BillingSession,
  Container,
  CreatedApiToken,
  Firewall,
  Key,
  Me,
  PermissionChain,
  Tier,
  UpdatedApiToken,
  UsageLine,
} from './types';

interface Fetched<T> {
  data?: T | undefined;
  error?: unknown;
  response: Response;
}

/**
 * Every request goes through here.
 *
 * The request id is minted first and sent on the wire: the API reuses a well-formed
 * client id verbatim and writes it into audit_events.request_id, so a click in this
 * dashboard becomes a row an operator can find. It is minted before the call so that a
 * transport failure — which never gets a response header — can still name one.
 */
export async function call<T>(ctx: CallCtx, run: (requestId: string) => Promise<Fetched<T>>): Promise<T> {
  const requestId = newRequestId();
  const full: CallCtx = { ...ctx, requestId };
  noteRequestId(requestId);
  try {
    const r = await run(requestId);
    if (r.error !== undefined || !r.response.ok) {
      throw RyptError.fromResponse(r.response, r.error, full);
    }
    // a 204 resolves to undefined, typed void at the call site
    return r.data as T;
  } catch (cause) {
    if (cause instanceof RyptError) throw cause;
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw RyptError.transport(cause, full);
  }
}

const rid = (id: string) => ({ 'x-request-id': id });

/** RequestInit.signal is `AbortSignal | null`, and exactOptionalPropertyTypes forbids an
 *  explicit undefined, so the property is spread in only when there is one. */
const sig = (signal?: AbortSignal) => (signal ? { signal } : {});

/* ---- keys: either credential ---- */

export const listKeys = (signal?: AbortSignal) =>
  call<Key[]>({ op: 'listKeys' }, (id) => api.GET('/v1/keys', { ...sig(signal), headers: rid(id) }));

export const getKey = (keyId: string, signal?: AbortSignal) =>
  call<Key>({ op: 'getKey', keyId }, (id) =>
    api.GET('/v1/keys/{id}', { params: { path: { id: keyId } }, ...sig(signal), headers: rid(id) }),
  );

/** `container_id` is where the key goes, for good; the dashboard always names the
 *  container whose page made it. */
export const createKey = (body: { name: string; tier: Tier; container_id: string }) =>
  call<Key>({ op: 'createKey', keyName: body.name, tier: body.tier }, (id) =>
    api.POST('/v1/keys', { body, headers: rid(id) }),
  );

/**
 * Renaming changes the name and nothing else, and answers with the key. The context names
 * the new name, because the refusals about a name — `invalid_name`, and `key_exists` for a
 * name another live key holds — are about the one asked for, not the one it had.
 */
export const renameKey = (keyId: string, name: string) =>
  call<Key>({ op: 'renameKey', keyId, keyName: name }, (id) =>
    api.PATCH('/v1/keys/{id}', { params: { path: { id: keyId } }, body: { name }, headers: rid(id) }),
  );

/**
 * Deleting a key puts it in pending deletion and answers with it: nothing is destroyed,
 * every operation on it is refused, and it can be restored for seven days. Deleting one
 * that is already pending changes nothing and answers the same.
 */
export const deleteKey = (keyId: string, keyName?: string) =>
  call<Key>({ op: 'deleteKey', keyId, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.DELETE('/v1/keys/{id}', { params: { path: { id: keyId } }, headers: rid(id) }),
  );

/** Takes a key out of pending deletion. A paid key is then billed as if it had never been
 *  deleted. A key that is not pending is `409 not_pending_deletion`. */
export const restoreKey = (keyId: string, keyName?: string) =>
  call<Key>({ op: 'restoreKey', keyId, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/restore', { params: { path: { id: keyId } }, headers: rid(id) }),
  );

export const rotateKey = (keyId: string, keyName?: string) =>
  call<Key>({ op: 'rotateKey', keyId, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/rotate', { params: { path: { id: keyId } }, headers: rid(id) }),
  );

/**
 * Retires one old version by hand, and answers with the key: the version keeps
 * decrypting, unwrapping and rewrapping for seven days, is listed in `retiring_versions`
 * with the time it is destroyed, and can be restored until then. The primary cannot be
 * retired (409 primary_version). Retiring one that is retiring already changes nothing.
 */
export const retireKeyVersion = (keyId: string, version: string, keyName?: string) =>
  call<Key>({ op: 'retireKeyVersion', keyId, version, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/versions/{version}/retire', {
      params: { path: { id: keyId, version } },
      headers: rid(id),
    }),
  );

/**
 * Takes a retiring version of a paid key out of retirement before its time, and answers
 * with the key: the version works as before and is never destroyed unless it is retired
 * again. From its `destroy_after` on it is 409 restore_window_closed.
 */
export const restoreKeyVersion = (keyId: string, version: string, keyName?: string) =>
  call<Key>({ op: 'restoreKeyVersion', keyId, version, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/versions/{version}/restore', {
      params: { path: { id: keyId, version } },
      headers: rid(id),
    }),
  );

/**
 * Sets how many versions a paid key's rotations keep, `null` for every one, and answers
 * with the key. Nothing is retired by this request: the setting takes effect at the
 * key's next rotation. A free key has no such setting (409 free_key_one_version).
 */
export const setKeyRetention = (keyId: string, keepAtMost: number | null, keyName?: string) =>
  call<Key>({ op: 'setKeyRetention', keyId, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/retention', {
      params: { path: { id: keyId } },
      body: { keep_at_most: keepAtMost },
      headers: rid(id),
    }),
  );

/* ---- audit ---- */

export interface AuditWindow {
  since?: string | undefined;
  until?: string | undefined;
  limit?: number | undefined;
  signal?: AbortSignal | undefined;
}

export const listAudit = (keyId: string, w: AuditWindow = {}) =>
  call<AuditEvent[]>({ op: 'listAuditEvents', keyId }, (id) =>
    api.GET('/v1/keys/{id}/audit', {
      params: {
        path: { id: keyId },
        // openapi-fetch drops undefined query params. That matters: a present-but-empty
        // since/until is REJECTED by the API, while an absent one takes the default.
        // Never pass ''.
        query: {
          ...(w.since ? { since: w.since } : {}),
          ...(w.until ? { until: w.until } : {}),
          limit: w.limit ?? AUDIT_DEFAULT_LIMIT,
        },
      },
      ...sig(w.signal),
      headers: rid(id),
    }),
  );

/* ---- usage ---- */

export const getUsage = (period: string, signal?: AbortSignal) =>
  call<UsageLine[]>({ op: 'getUsage', period }, (id) =>
    api.GET('/v1/usage', { params: { query: { period } }, ...sig(signal), headers: rid(id) }),
  );

/* ---- console: Auth0 token only ---- */

export const getMe = (signal?: AbortSignal) =>
  call<Me>({ op: 'getMe' }, (id) => api.GET('/v1/me', { ...sig(signal), headers: rid(id) }));

export const listApiTokens = (signal?: AbortSignal) =>
  call<ApiToken[]>({ op: 'listApiTokens' }, (id) => api.GET('/v1/api-tokens', { ...sig(signal), headers: rid(id) }));

/**
 * The only function in the codebase that returns a plaintext API token.
 *
 * Its single caller is the createApiToken mutation, which strips `token` and `prefix`
 * before anything else can see the value. Nothing else may call this.
 */
export const createApiTokenRaw = (body: { name: string; container_id: string; allowed_actions: Action[] }) =>
  call<CreatedApiToken>({ op: 'createApiToken', keyName: body.name }, (id) =>
    api.POST('/v1/api-tokens', { body, headers: rid(id) }),
  );

/**
 * Delete now: destroys the key at once, pending or not, and answers 204. From then on it
 * is 404 everywhere, and its name, and a free key's place, are free. Console only, like
 * minting an API token: an API token that leaked can delete keys, which waits seven days and
 * can be undone, but it cannot destroy them.
 */
export const destroyKey = (keyId: string, keyName?: string) =>
  call<void>({ op: 'destroyKey', keyId, ...(keyName === undefined ? {} : { keyName }) }, (id) =>
    api.POST('/v1/keys/{id}/destroy', { params: { path: { id: keyId } }, headers: rid(id) }),
  );

/**
 * Restricts an API token, by the networks it may be used from, the keys it may act on and
 * what it may do with them. Each field the body carries replaces that restriction, `null`
 * lifts the first two, and a field it leaves out stays as it was — so the dialog sends
 * only what the user changed. The answer carries the container's permission chain head.
 */
export const updateApiToken = (apiTokenId: string, body: ApiTokenRestrictions, name?: string | null) =>
  call<UpdatedApiToken>({ op: 'updateApiToken', keyId: apiTokenId, ...(name ? { keyName: name } : {}) }, (id) =>
    api.PATCH('/v1/api-tokens/{id}', { params: { path: { id: apiTokenId } }, body, headers: rid(id) }),
  );

/**
 * The head of a container's permission chain: the latest entry of its record of every
 * change to what its API tokens may do. The first read of a container's head may start its
 * chain, which the API does itself.
 */
export const getPermissionChain = (containerId: string, signal?: AbortSignal) =>
  call<PermissionChain>({ op: 'getPermissionChain' }, (id) =>
    api.GET('/v1/containers/{id}/permission-chain', {
      ...sig(signal),
      params: { path: { id: containerId } },
      headers: rid(id),
    }),
  );

/* ---- containers and firewalls: Auth0 token only ---- */

export const listContainers = (signal?: AbortSignal) =>
  call<Container[]>({ op: 'listContainers' }, (id) => api.GET('/v1/containers', { ...sig(signal), headers: rid(id) }));

export const createContainer = (name: string) =>
  call<Container>({ op: 'createContainer', keyName: name }, (id) =>
    api.POST('/v1/containers', { body: { name }, headers: rid(id) }),
  );

export const renameContainer = (containerId: string, name: string) =>
  call<Container>({ op: 'renameContainer', keyId: containerId, keyName: name }, (id) =>
    api.PATCH('/v1/containers/{id}', { params: { path: { id: containerId } }, body: { name }, headers: rid(id) }),
  );

/** Only an empty container that is not the default; 409 says which rule it broke. */
export const deleteContainer = (containerId: string, name: string) =>
  call<void>({ op: 'deleteContainer', keyId: containerId, keyName: name }, (id) =>
    api.DELETE('/v1/containers/{id}', { params: { path: { id: containerId } }, headers: rid(id) }),
  );

export const listFirewalls = (signal?: AbortSignal) =>
  call<Firewall[]>({ op: 'listFirewalls' }, (id) => api.GET('/v1/firewalls', { ...sig(signal), headers: rid(id) }));

export const createFirewall = (body: { container_id: string; name: string; allowed_ips: string[] }) =>
  call<Firewall>({ op: 'createFirewall', keyName: body.name }, (id) =>
    api.POST('/v1/firewalls', { body, headers: rid(id) }),
  );

/** A field left out stays as it was, so an edit sends only what changed. */
export const updateFirewall = (firewallId: string, body: { name?: string; allowed_ips?: string[] }, name: string) =>
  call<Firewall>({ op: 'updateFirewall', keyId: firewallId, keyName: body.name ?? name }, (id) =>
    api.PATCH('/v1/firewalls/{id}', { params: { path: { id: firewallId } }, body, headers: rid(id) }),
  );

/** Only while no live API token is held to it; 409 firewall_in_use otherwise. */
export const deleteFirewall = (firewallId: string, name: string) =>
  call<void>({ op: 'deleteFirewall', keyId: firewallId, keyName: name }, (id) =>
    api.DELETE('/v1/firewalls/{id}', { params: { path: { id: firewallId } }, headers: rid(id) }),
  );

export const revokeApiToken = (apiTokenId: string, name?: string | null) =>
  call<void>(
    { op: 'revokeApiToken', keyId: apiTokenId, ...(name ? { keyName: name } : {}) },
    (id) => api.DELETE('/v1/api-tokens/{id}', { params: { path: { id: apiTokenId } }, headers: rid(id) }),
  );

/* ---- billing: Auth0 token only, and only where the API configures billing ---- */

/**
 * The tenant's standing and this month's estimate, read from rypt's own database.
 *
 * Where the API has no billing configured this path is not a route and answers 404.
 * That is a fact about the deployment, not a failure, and `useBilling` turns it into
 * `null` rather than letting `not_found`'s "no such key" copy reach a screen.
 */
export const getBilling = (signal?: AbortSignal) =>
  call<Billing>({ op: 'getBilling' }, (id) => api.GET('/v1/billing', { ...sig(signal), headers: rid(id) }));

/*
 * The two Stripe hand-offs. Each answers with the URL of a page on Stripe — Checkout to
 * start a subscription, the customer portal to manage one — made for this tenant just
 * now. Their only caller is the billing-session mutations, which follow the URL at once
 * and keep it nowhere: a Checkout URL stays open for 24 hours, and a page on Stripe that
 * belongs to this tenant is not something to leave lying in a cache.
 */

export const createCheckoutSession = () =>
  call<BillingSession>({ op: 'createCheckoutSession' }, (id) =>
    api.POST('/v1/billing/checkout', { headers: rid(id) }),
  );

export const createPortalSession = () =>
  call<BillingSession>({ op: 'createPortalSession' }, (id) =>
    api.POST('/v1/billing/portal', { headers: rid(id) }),
  );
